import { FieldArray, Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import Input from '../../../components/form-control/Input';
import Button from '../../../components/form-control/Button';
import { Plus, Send, Trash2 } from 'lucide-react';
import {
  deleteNotificationContact,
  getNotificationContacts,
  getSettingNotifications,
  saveNotificationContacts,
  sendSettingNotifications
} from '../../../api/requests';
import { ITeamNotification } from '../../../resources/interfaces';
import {
  getFormattedPhoneNumber,
  getPhoneNumberFromShortCodeFormat,
  showToast
} from '../../../utils/miscFunctions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Loader from '../../../components/Loader';
import IntlPhoneInput from '../../../components/form-control/IntlPhoneInput/IntlPhoneInput';

interface INotificationData {
  email: {
    systemMaintenance: boolean;
    systemAlerts: boolean;
    billing: boolean;
    marketing: boolean;
  };
  sms: {
    emergencyAlerts: boolean;
  };
}
interface INotificationSettings {
  id: number;
  companyId: number;
  companyName: string;
  emailAddress: string;
  phoneNumber: string;
  emailEnabled: boolean;
  phoneEnabled: boolean;
  smsEnabled: boolean;
  createdOn: string;
  updatedOn: string;
}

interface INotificationContactFormValue {
  id?: number;
  email: string;
  phone: string;
}

const EMPTY_CONTACT: INotificationContactFormValue = { email: '', phone: '' };

const NotificationSettings = () => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(true);
  const [notification, setNotification] = useState<INotificationSettings>();
  const [enableEmailNotification, setEnableEmailNotification] = useState<boolean>(false);
  const [enableSmsNotification, setEnableSmsNotification] = useState<boolean>(false);
  const [deletedContactIds, setDeletedContactIds] = useState<number[]>([]);
  const [notificationData, setNotificationData] = useState<INotificationData>({
    email: {
      systemMaintenance: true,
      systemAlerts: true,
      billing: true,
      marketing: true
    },
    sms: {
      emergencyAlerts: true
    }
  });
  const smsNotificationItems = Object.entries(notificationData.sms);
  const emailNotificationItems = Object.entries(notificationData.email);

  const handleEmailToggler = () => {
    return (
      <div
        onClick={() => setEnableEmailNotification(!enableEmailNotification)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none  focus:ring-offset-2 ${enableEmailNotification ? 'bg-primary-main' : 'bg-gray-200'
          }`}>
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enableEmailNotification ? 'translate-x-5' : 'translate-x-0'
            }`}
        />
      </div>
    );
  };

  const handleSMSToggler = () => {
    return (
      <div
        onClick={() => setEnableSmsNotification(!enableSmsNotification)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none  focus:ring-offset-2 ${enableSmsNotification ? 'bg-primary-main' : 'bg-gray-200'
          }`}>
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enableSmsNotification ? 'translate-x-5' : 'translate-x-0'
            }`}
        />
      </div>
    );
  };

  const handleEmailTogglerBottons = (key: string, value: boolean) => {
    return (
      <div
        onClick={() =>
          setNotificationData({
            ...notificationData,
            email: { ...notificationData.email, [key]: enableEmailNotification && !value }
          })
        }
        className={`relative inline-flex h-6 w-11 flex-shrink-0 ${enableEmailNotification ? 'cursor-pointer' : ''} rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none  focus:ring-offset-2 ${value && enableEmailNotification ? 'bg-gray-600' : 'bg-gray-200'
          }`}>
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${value && enableEmailNotification ? 'translate-x-5' : 'translate-x-0'
            }`}
        />
      </div>
    );
  };

  const handleSMSTogglerBottons = (key: string, value: boolean) => {
    return (
      <div
        onClick={() =>
          setNotificationData({
            ...notificationData,
            sms: { ...notificationData.sms, [key]: enableSmsNotification && !value }
          })
        }
        className={`relative inline-flex h-6 w-11 flex-shrink-0 ${enableSmsNotification ? 'cursor-pointer' : ''} rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none  focus:ring-offset-2 ${value && enableSmsNotification ? 'bg-gray-600' : 'bg-gray-200'
          }`}>
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${value && enableSmsNotification ? 'translate-x-5' : 'translate-x-0'
            }`}
        />
      </div>
    );
  };

  const sendNotification = (payload: ITeamNotification) => {
    return sendSettingNotifications(payload)
      .then((response: any) => {
        return response;
      })
      .catch((error: any) => {
        throw error;
      });
  }

  const getNotification = () => {
    getSettingNotifications()
      .then((response: any) => {
        setNotification(response.data);
        if (response) {
          setEnableEmailNotification(response.data.emailEnabled);
          setEnableSmsNotification(response.data.phoneEnabled);
        }
      })
      .catch((error: any) => {
        console.log('error', error?.description);
      })
      .finally(() => setLoading(false));
  };

  useQuery({ queryKey: ['getNotification'], queryFn: getNotification });

  const { data: existingContacts, isLoading: contactsLoading, refetch: refetchContacts } = useQuery<INotificationContactFormValue[]>({
    queryKey: ['getNotificationContacts'],
    queryFn: async () => {
      const response: any = await getNotificationContacts();
      const raw = Array.isArray(response) ? response : response?.data ?? [];
      return raw.map((c: any) => ({
        id: c.id,
        email: c.email ?? '',
        phone: c.phone ?? ''
      }));
    }
  });

  const initialContacts: INotificationContactFormValue[] = (() => {
    if (existingContacts && existingContacts.length > 0) return existingContacts;
    return [{ ...EMPTY_CONTACT }];
  })();

  const persistContacts = async (contacts: INotificationContactFormValue[]) => {
    await Promise.all(
      deletedContactIds.map((contactId) => deleteNotificationContact(contactId).catch(() => null))
    );
    // getFormattedPhoneNumber isn't idempotent for malformed values — re-applying it on every
    // submit kept prepending '234' to numbers the user never touched. Only format when the
    // phone is new or actually changed in this session.
    const originalPhoneById = new Map<number, string>(
      (existingContacts ?? [])
        .filter((c: INotificationContactFormValue) => c.id != null)
        .map((c: INotificationContactFormValue) => [c.id as number, (c.phone ?? '').trim()] as [number, string])
    );
    const payload = contacts
      .filter((c) => (c.email ?? '').trim() || (c.phone ?? '').trim())
      .map((c) => {
        const phoneRaw = (c.phone ?? '').trim();
        const isUnchanged = c.id != null && originalPhoneById.get(c.id) === phoneRaw;
        return {
          id: c.id,
          email: (c.email ?? '').trim(),
          phone: isUnchanged ? phoneRaw : getFormattedPhoneNumber(phoneRaw),
        };
      });
    await saveNotificationContacts(payload);
  };

  if (loading || contactsLoading) {
    return (
      <Loader message="Please wait..." brollyLoaderFixed />
    );
  }

  return (
    <div className="space-y-6">
      <Formik
        initialValues={{
          email: notification?.emailAddress ?? '',
          phone: getPhoneNumberFromShortCodeFormat(String(notification?.phoneNumber)) ?? 0,
          contacts: initialContacts
        }}
        enableReinitialize
        validationSchema={Yup.object().shape({
          email: Yup.string().when([], {
            is: () => enableEmailNotification,
            then: (schema) => schema.email('Invalid email').required('Email is required'),
          }),
          phone: Yup.string().when([], {
            is: () => enableSmsNotification,
            then: (schema) => schema.required('Phone number is required')
          }),
          contacts: Yup.array().of(
            Yup.object().shape({
              email: Yup.string().trim().email('Invalid email'),
              phone: Yup.string().trim()
            }).test('at-least-one', 'Provide an email or phone number.', (value) => {
              if (!value) return true;
              return Boolean((value.email ?? '').trim() || (value.phone ?? '').trim());
            })
          )
        })}
        onSubmit={async (values) => {
          const payload = {
            emailAddress: values.email,
            emailEnabled: enableEmailNotification,
            phoneNumber: getFormattedPhoneNumber(values.phone),
            smsEnabled: enableSmsNotification
          };
          try {
            await sendNotification(payload);
            await persistContacts(values.contacts);
            setDeletedContactIds([]);
            await refetchContacts();
            queryClient.invalidateQueries({ queryKey: ['getCompanyOnboardingStatus'] });
            showToast.success('Notification settings saved successfully');
          } catch (error: any) {
            showToast.error(error?.description ?? 'Failed to save notification settings');
          }
        }}>
        {({ values, errors, touched, handleSubmit, handleBlur, handleChange, setFieldValue }) => (
          <form className=" py-[30px] bg-white dark:bg-darkBg min-h-full" onSubmit={handleSubmit}>
            <div className="mb-6">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">
                  Notification Recipients
                </h3>
                <p className="text-sm text-gray-500 dark:text-darkBg-subText mt-1">
                  Add email addresses and phone numbers that should receive alerts.
                </p>
              </div>

              <FieldArray name="contacts">
                {({ push, remove }) => (
                  <div className="flex flex-col gap-4">
                    {values.contacts?.map((contact: INotificationContactFormValue, index: number) => {
                      const contactErrors = (errors.contacts as any)?.[index];
                      const contactTouched = (touched.contacts as any)?.[index];
                      const emailError = typeof contactErrors === 'string'
                        ? contactErrors
                        : contactErrors?.email;
                      const phoneError = typeof contactErrors === 'string'
                        ? contactErrors
                        : contactErrors?.phone;
                      return (
                        <div key={index} className="flex items-start gap-3">
                          <div className="flex-1">
                            <Input
                              type="email"
                              name={`contacts.${index}.email`}
                              value={contact.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Email"
                              error={contactTouched?.email ? emailError : undefined}
                              helperText={contactTouched?.email ? emailError : undefined}
                              className="rounded"
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              type="tel"
                              name={`contacts.${index}.phone`}
                              value={contact.phone}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Phone number"
                              error={contactTouched?.phone ? phoneError : undefined}
                              helperText={contactTouched?.phone ? phoneError : undefined}
                              className="rounded"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (contact.id) {
                                setDeletedContactIds((prev) => [...prev, contact.id as number]);
                              }
                              remove(index);
                            }}
                            disabled={values.contacts.length <= 1}
                            className="mt-2 p-2 text-gray-500 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label="Remove contact">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => push({ ...EMPTY_CONTACT })}
                      className="flex items-center gap-2 text-sm text-primary-main hover:underline w-fit">
                      <Plus className="h-4 w-4" />
                      Add Another
                    </button>
                  </div>
                )}
              </FieldArray>
            </div>

            <div className="py-4 border-t border-gray-200 dark:border-darkBg-border flex items-end justify-end">
              <div className="flex items-center space-x-3">
                <Button
                  label=" Submit"
                  type="submit"
                  size="lg"
                  curved
                  bgColor='primary'
                  icon={<Send className="mr-2 h-4 w-4" />}
                />
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default NotificationSettings;
